import { useCurrentRoom } from "@/state/selectors";
import { SplashScreen } from "@/components/screens/SplashScreen/SplashScreen";
import { PlayingScreen } from "@/components/screens/PlayingScreen/PlayingScreen";
import { useCacheStatus } from "@/state/cacheStoreHooks";
import { useCacheStore } from "@/state/cacheStore";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useCallback, useEffect, useState } from "react";
import { AppBaseLayout } from "@/components/layout/AppBaseLayout";
import DevTools from "@/components/dev/DevTools";
import {usePreloadIntroAssets} from "@/libs/assetPreloading/usePreloadIntroAssets";

export function GameRoot() {
    const { status, error } = useCacheStatus();
    const [showDevTools, setShowDevTools] = useState<boolean>(false);
    const [backgroundReady, setBackgroundReady] = useState(false);
    const [splashFadingOut, setSplashFadingOut] = useState(false);
    const [splashDismissed, setSplashDismissed] = useState(false);
    const fetchData = useCacheStore((state) => state.fetchData);
    const currentRoom = useCurrentRoom();
    const initialRoomSelected = currentRoom !== null;

    const handleBackgroundReady = useCallback(() => {
        setBackgroundReady(true);
    }, []);

    useDocumentMeta();
    usePreloadIntroAssets();

    useEffect(() => {
        if (status === "idle") {
            fetchData();
        }
    }, [status, fetchData]);

    useEffect(() => {
        if (backgroundReady && initialRoomSelected && status === "success" && !splashFadingOut && !splashDismissed) {
            setSplashFadingOut(true);
        }
    }, [backgroundReady, initialRoomSelected, status, splashFadingOut, splashDismissed]);

    const handleSplashTransitionEnd = useCallback(() => {
        if (splashFadingOut) {
            setSplashDismissed(true);
        }
    }, [splashFadingOut]);

    useEffect(() => {
        if (!initialRoomSelected) {
            setSplashFadingOut(false);
            setSplashDismissed(false);
            setBackgroundReady(false);
        }
    }, [initialRoomSelected]);

    if (status === "error") {
        return (
            <div className="flex size-full flex-col items-center justify-center gap-4 p-4">
                <h1 className="text-2xl font-bold text-red-600">Failed to load game</h1>
                <p className="text-gray-600">{error?.message ?? "Unknown error"}</p>
                <button onClick={fetchData} className="rounded bg-blue-500 px-6 py-2 text-white hover:bg-blue-600">
                    Retry
                </button>
            </div>
        );
    }

    if (showDevTools) {
        return (
            <AppBaseLayout restrictWidth={false}>
                <DevTools onClose={() => setShowDevTools(false)} />
            </AppBaseLayout>
        );
    }

    return (
        <AppBaseLayout>
            {!splashDismissed && (
                <div
                    className={`absolute inset-0 z-10 transition-opacity duration-200 ${splashFadingOut ? "opacity-0" : "opacity-100"}`}
                    onTransitionEnd={handleSplashTransitionEnd}
                >
                    <SplashScreen onDevToolsClick={() => setShowDevTools(true)} />
                </div>
            )}
            <PlayingScreen isActive={splashDismissed} onBackgroundReady={handleBackgroundReady} />
        </AppBaseLayout>
    );
}